// ── Shared password reveal toggle ──
//
// One eye button that reveals/masks every `.password-field .form-input` inside
// a form together. Hoisted out of pages/login.js and pages/security-settings.js,
// which each carried a byte-identical copy (TPT194 — the reset-password page
// needed a third). DOM-touching, so — like lib/avatar-pick.js — not
// node-testable; the pure attribute triple lives in lib/signup-form.js's
// passwordToggleAttrs(), which is.
//
// Markup contract (styles.css `.password-field` / `.password-toggle`): the
// input that owns the button sits in `<div class="password-field has-toggle">`
// next to passwordToggleButton(); a confirm input sits in a bare
// `<div class="password-field">`. Only ONE button per form — it drives all the
// fields inside it.

import { passwordToggleAttrs } from './signup-form.js';

// Feather-style eye / eye-off. aria-hidden — the button (not the icon) carries
// the accessible label.
export const EYE_ICON = '<svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>';
export const EYE_OFF_ICON = '<svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>';

// The button's initial (masked) markup. `type="button"` is what keeps its
// click out of the form's submit path.
export function passwordToggleButton(id) {
  return `<button type="button" class="password-toggle" id="${id}" aria-label="Show password" aria-pressed="false" title="Show password">${EYE_ICON}</button>`;
}

// Shared eye toggle for a form's password + confirm inputs — a confirm field
// only earns its keep if the two typed values can be compared side by side, so
// one click reveals/masks both together and the single button's aria-pressed
// state stays in sync. `scope` restricts the query to this form so the toggle
// can never reach an unrelated password input elsewhere in the DOM.
//
// State lives ONLY in the DOM (`input.type`), never in a module-local
// variable: callers re-render their pane by replacing innerHTML wholesale,
// rebuilding it from a template that hardcodes type="password" /
// aria-pressed="false" — that's what makes "always comes back masked" true
// with no reset code to remember. A module-level flag would survive the
// re-render and silently re-reveal the fields, which is exactly the bug this
// avoids.
export function togglePasswordVisibility(scope) {
  const btn = scope.querySelector('.password-toggle');
  const revealed = btn.getAttribute('aria-pressed') !== 'true';
  const { type, label, pressed } = passwordToggleAttrs(revealed);
  for (const input of scope.querySelectorAll('.password-field .form-input')) {
    // Assigning .type resets the caret to the end in some engines; both
    // 'password' and 'text' support the selection API, so save/restore it.
    const { selectionStart, selectionEnd } = input;
    input.type = type;
    if (document.activeElement === input && selectionStart !== null) {
      input.setSelectionRange(selectionStart, selectionEnd);
    }
  }
  btn.setAttribute('aria-pressed', pressed);
  btn.setAttribute('aria-label', label);
  btn.title = label;
  btn.innerHTML = revealed ? EYE_OFF_ICON : EYE_ICON;
}

// Wire the form's toggle button, if it has one. Declines focus on mousedown so
// the caret stays in whatever field is being typed in — without this the
// button takes focus and the selection restore in togglePasswordVisibility()
// never has anything to restore.
export function bindPasswordToggle(scope) {
  const btn = scope.querySelector('.password-toggle');
  if (!btn) return;
  btn.addEventListener('mousedown', (e) => e.preventDefault());
  btn.addEventListener('click', () => togglePasswordVisibility(scope));
}
